import { Mutation, Action, VuexModule, getModule, Module } from 'vuex-module-decorators';
import { store } from '../..';
import { Teacher } from '@/models/teacher/teacher';
import { ImportTeacherResult } from '@/models/teacher/import-teacher-result';
import { GradeSchoolClass, SchoolClassSelection } from '@/models/school-class/school-class';
import { teacherService } from '@/service/api/teacher.service';
import FileSaver from 'file-saver';
import { TeacherSearch } from '@/models/teacher/teacher-search';
import { TeacherPasswordRequest } from '@/models/teacher/teacher-password-request';
import { TeacherRequest } from '@/models/teacher/teacher-request';

export interface ITeacherState {
  teachers: Teacher[]; // 教員一覧
  importTeachersResults: ImportTeacherResult; // 教員インポートレスポンスパラメータ
  teacher: Teacher; // 教員情報詳細
  selectionSchoolClass: SchoolClassSelection[]; // 教員割り当てクラス一覧(チェックボックスグループ)
}
@Module({ dynamic: true, store, name: 'teacherModule', namespaced: true })
class TeacherModule extends VuexModule implements ITeacherState {
  teachers: Teacher[] = [];
  importTeachersResults: ImportTeacherResult = new ImportTeacherResult();
  teacher: Teacher = new Teacher();
  selectionSchoolClass: SchoolClassSelection[] = [];

  get isAllAssignSchoolClasses(): boolean {
    if (this.teachers.length === 0) {
      return false;
    }
    return this.teachers.every((teacher: Teacher) => teacher.isAdmin || teacher.schoolClasses.length > 0);
  }

  get selectedSchoolClassIDs(): number[] {
    return this.selectionSchoolClass.map((v: SchoolClassSelection) => v.checkAllGroup).flat();
  }

  get isSelectedSchoolClass(): boolean {
    return this.selectionSchoolClass.some((schoolClassByGrade: SchoolClassSelection) => {
      return schoolClassByGrade.isChecked();
    });
  }

  get validTeachers(): Teacher[] {
    return this.teachers.filter((teacher: Teacher) => teacher.isValid);
  }

  @Mutation
  initSelectionSchoolClass(gradeSchoolClasses: GradeSchoolClass[]) {
    this.selectionSchoolClass = [];
    gradeSchoolClasses.forEach((v: GradeSchoolClass, _: number) => {
      this.selectionSchoolClass.push(new SchoolClassSelection(v.grade, teacherModule.teacher.schoolClasses, v.schoolClasses));
    });
  }

  @Mutation
  setTeachers(value: Teacher[]) {
    this.teachers = value;
  }

  @Mutation
  setTeacherDetail(value: Teacher) {
    this.teacher = value;
  }

  @Mutation
  setImportTeachersResults(value: ImportTeacherResult) {
    this.importTeachersResults = value;
  }

  @Mutation
  clearTeacherImportResults() {
    this.importTeachersResults = undefined;
  }

  @Mutation
  clearTeacher() {
    this.teacher = new Teacher();
  }

  @Mutation
  setSelectionSchoolClass(value: SchoolClassSelection[]) {
    this.selectionSchoolClass = value;
  }

  /**
   * 教員一覧取得
   * @param teacherSearch 教員一覧用リクエストパラメータ
   */
  @Action({ rawError: true })
  async fetchTeacherList(teacherSearch: TeacherSearch) {
    const result = await teacherService.getTeachers(teacherSearch.sortKey, teacherSearch.sortType);
    this.setTeachers(result);
  }

  /**
   * 教員情報取得
   * @param teacherID 教員ID
   */
  @Action({ rawError: true })
  async fetchTeacherDetail(teacherID: number) {
    const result = await teacherService.getTeacherDetail(teacherID);
    this.setTeacherDetail(result);
  }

  /**
   * 教員情報更新
   */
  @Action({ rawError: true })
  async updateTeacherDetail() {
    let requestParam: TeacherRequest = {
      email: this.teacher.email,
      firstName: this.teacher.firstName,
      lastName: this.teacher.lastName,
      password: this.teacher.password,
      teacherID: this.teacher.teacherID,
      schoolClassIDs: this.selectedSchoolClassIDs,
      role: this.teacher.role,
      isValid: this.teacher.isValid,
    };
    return await teacherService.updateTeacherDetail(requestParam);
  }

  /**
   * 教員パスワード更新
   * @param params 教員パスワード更新用リクエスト
   */
  @Action({ rawError: true })
  async updateTeacherPassword(params: TeacherPasswordRequest) {
    let teacherID = this.teacher.teacherID;
    return await teacherService.updateTeacherPassword(teacherID, params);
  }

  /**
   * 教員登録用エクセルダウンロード
   */
  @Action({ rawError: true })
  async downloadRegistrationTeacher() {
    const isUpdate = false;
    const file = await teacherService.downloadTeacherRegistrationTemplate(isUpdate);
    FileSaver.saveAs(file, '先生一括登録用テンプレート.xlsx');
  }

  /**
   * 教員更新用エクセルダウンロード
   */
  @Action({ rawError: true })
  async downloadUpdateTeacher(hasInvalid: boolean) {
    const isUpdate = true;
    const file = await teacherService.downloadTeacherRegistrationTemplate(isUpdate, hasInvalid);
    FileSaver.saveAs(file, '先生一括更新用テンプレート.xlsx');
  }

  /**
   * 教員アップロード
   * @param formData エクセルデータ
   */
  @Action({ rawError: true })
  async postTeachersExcel(formData: FormData) {
    return await teacherService.postTeachersExcel(formData);
  }

  /**
   * 教員情報追加
   */
  @Action({ rawError: true })
  async addTeacherDetail() {
    const detail = this.teacher;
    let params: TeacherRequest = {
      email: detail.email,
      firstName: detail.firstName,
      lastName: detail.lastName,
      password: detail.password,
      role: detail.role,
      schoolClassIDs: this.selectedSchoolClassIDs,
    };
    return await teacherService.addTeacherDetail(params);
  }
}

export const teacherModule = getModule(TeacherModule);
